import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Clock, UtensilsCrossed, IceCream2, Sparkles, Baby } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { restaurantMenu } from "../data/menuData";
import { translateDish } from "../i18n/dishNames";

export default function Home() {
  const { t, lang, tx } = useLang();
  const menusSection = restaurantMenu.find((s) => s.id === "menus");
  const oasis = menusSection?.items?.[0];
  const enfant = menusSection?.items?.[1];

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section className="min-h-screen flex items-center pt-32 pb-20 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl w-full grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span
              data-testid="hero-kicker"
              className="inline-block text-xs sm:text-sm uppercase tracking-[0.4em] text-amber-200/90 mb-5"
            >
              ✦ {t.hero_kicker} ✦
            </span>
            <h1
              data-testid="hero-title"
              className="font-serif text-6xl sm:text-7xl lg:text-8xl text-white leading-[0.95] mb-6"
            >
              L'<em className="italic text-amber-200 font-light">Oasis</em>
            </h1>
            <p className="font-serif text-xl sm:text-2xl text-amber-100/90 italic mb-6 leading-snug">
              {t.hero_subtitle}
            </p>
            <p className="text-base text-white/70 max-w-xl mb-10 leading-relaxed">
              {t.hero_description}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/restaurant"
                data-testid="cta-restaurant"
                className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-300 text-emerald-950 font-medium hover:bg-amber-200 transition shadow-lg shadow-amber-500/20"
              >
                <UtensilsCrossed className="w-4 h-4" />
                {t.hero_cta_restaurant}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </Link>
              <Link
                to="/gourmandise"
                data-testid="cta-gourmandise"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full glass-pill text-white hover:text-amber-200 transition"
              >
                <IceCream2 className="w-4 h-4" />
                {t.hero_cta_gourmandise}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </Link>
            </div>
          </div>

          {/* decorative glass panel */}
          <div className="md:col-span-5">
            <div className="relative">
              <div className="glass-card p-2 rotate-2 hover:rotate-0 transition-transform duration-700">
                <img
                  src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80&auto=format&fit=crop"
                  alt="L'Oasis ambiance"
                  className="w-full h-[420px] sm:h-[520px] object-cover rounded-3xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-card p-5 max-w-[230px] -rotate-3">
                <div className="font-serif text-amber-200 text-2xl mb-1">★★★★★</div>
                <p className="text-xs text-white/75 leading-relaxed">
                  « Une véritable parenthèse tropicale, des saveurs incroyables. »
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE TWO MENUS */}
      <section className="py-20 px-4 sm:px-6" data-testid="explore-section">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.4em] text-amber-200/80">
              ✦ {t.explore_subtitle} ✦
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl text-white mt-4">{t.explore_title}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Restaurant card */}
            <Link
              to="/restaurant"
              data-testid="card-restaurant"
              className="group relative overflow-hidden rounded-3xl glass-card p-2 hover:-translate-y-1 transition duration-500"
            >
              <div className="relative h-[360px] sm:h-[440px] overflow-hidden rounded-[1.3rem]">
                <img
                  src="https://images.unsplash.com/photo-1559737558-2f5a35f4523b?w=1400&q=80&auto=format&fit=crop"
                  alt="Carte Restaurant"
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/50 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <UtensilsCrossed className="w-6 h-6 text-amber-300 mb-3" />
                  <h3 className="font-serif text-3xl sm:text-4xl text-white mb-2">
                    {t.card_restaurant_title}
                  </h3>
                  <p className="text-sm text-white/75 mb-4 max-w-sm">{t.card_restaurant_desc}</p>
                  <span className="inline-flex items-center gap-2 text-amber-200 text-sm font-medium">
                    {t.discover}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Gourmandise card */}
            <Link
              to="/gourmandise"
              data-testid="card-gourmandise"
              className="group relative overflow-hidden rounded-3xl glass-card p-2 hover:-translate-y-1 transition duration-500"
            >
              <div className="relative h-[360px] sm:h-[440px] overflow-hidden rounded-[1.3rem]">
                <img
                  src="https://customer-assets.emergentagent.com/job_menu-showcase-109/artifacts/lg0eqflz_Gemini_Generated_Image_ysy4aaysy4aaysy4.png"
                  alt="Carte Gourmandise"
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/50 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <IceCream2 className="w-6 h-6 text-amber-300 mb-3" />
                  <h3 className="font-serif text-3xl sm:text-4xl text-white mb-2">
                    {t.card_gourmandise_title}
                  </h3>
                  <p className="text-sm text-white/75 mb-4 max-w-sm">{t.card_gourmandise_desc}</p>
                  <span className="inline-flex items-center gap-2 text-amber-200 text-sm font-medium">
                    {t.discover}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURED MENUS */}
      <section className="py-20 px-4 sm:px-6" data-testid="featured-menus-section">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.4em] text-amber-200/80">
              ✦ {t.featured_subtitle} ✦
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl text-white mt-4">
              {t.featured_title}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {oasis && (
              <article
                data-testid="featured-menu-oasis"
                className="relative glass-card p-8 sm:p-10 overflow-hidden group hover:-translate-y-1 transition duration-500"
              >
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" />
                <div className="flex items-start justify-between mb-5 relative">
                  <div className="flex items-center gap-3">
                    <div className="glass-pill p-3">
                      <Sparkles className="w-5 h-5 text-amber-300" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-amber-200/80">
                      L'Oasis · Signature
                    </span>
                  </div>
                  <span className="font-serif text-3xl sm:text-4xl text-amber-200">
                    {oasis.price}
                  </span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-white mb-4">
                  {translateDish(oasis.name, lang)}
                </h3>
                <p className="text-sm sm:text-base text-white/75 leading-relaxed mb-6">
                  {tx(oasis.desc)}
                </p>
                <Link
                  to="/restaurant#menus"
                  className="inline-flex items-center gap-2 text-amber-200 hover:text-amber-100 text-sm font-medium"
                >
                  {t.discover}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </Link>
              </article>
            )}
            {enfant && (
              <article
                data-testid="featured-menu-enfant"
                className="relative glass-card p-8 sm:p-10 overflow-hidden group hover:-translate-y-1 transition duration-500"
              >
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-emerald-300/15 blur-3xl pointer-events-none" />
                <div className="flex items-start justify-between mb-5 relative">
                  <div className="flex items-center gap-3">
                    <div className="glass-pill p-3">
                      <Baby className="w-5 h-5 text-amber-300" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-amber-200/80">
                      L'Oasis · Kids
                    </span>
                  </div>
                  <span className="font-serif text-3xl sm:text-4xl text-amber-200">
                    {enfant.price}
                  </span>
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-white mb-4">
                  {translateDish(enfant.name, lang)}
                </h3>
                <p className="text-sm sm:text-base text-white/75 leading-relaxed mb-6">
                  {tx(enfant.desc)}
                </p>
                <Link
                  to="/restaurant#menus"
                  className="inline-flex items-center gap-2 text-amber-200 hover:text-amber-100 text-sm font-medium"
                >
                  {t.discover}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </Link>
              </article>
            )}
          </div>
        </div>
      </section>

      {/* INFO */}
      <section className="py-20 px-4 sm:px-6" data-testid="info-section">
        <div className="mx-auto max-w-5xl glass-card p-8 sm:p-12">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.4em] text-amber-200/80">
              ✦ {t.info_title} ✦
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mt-3">L'Oasis</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="glass-pill p-3 mb-3">
                <MapPin className="w-5 h-5 text-amber-300" />
              </div>
              <h4 className="text-amber-200 text-sm uppercase tracking-wider mb-2">
                {t.info_address}
              </h4>
              <p className="text-white/80 text-sm">{t.info_address_value}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="glass-pill p-3 mb-3">
                <Phone className="w-5 h-5 text-amber-300" />
              </div>
              <h4 className="text-amber-200 text-sm uppercase tracking-wider mb-2">
                {t.info_phone}
              </h4>
              <p className="text-white/80 text-sm">{t.info_phone_value}</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="glass-pill p-3 mb-3">
                <Clock className="w-5 h-5 text-amber-300" />
              </div>
              <h4 className="text-amber-200 text-sm uppercase tracking-wider mb-2">
                {t.info_hours}
              </h4>
              <p className="text-white/80 text-sm">{t.info_hours_value}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
